import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAdminPref, ActiveOnlySwitch } from "../../lib/adminPrefs";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { DownloadSimple, Gear, FloppyDisk, Trash } from "@phosphor-icons/react";
import { startDownload, backendDownloadUrl, downloadFromBackend } from "../../lib/download";
import DestructiveConfirmModal from "../../components/DestructiveConfirmModal";
// P4-N5B Slice C4 R1 — canonical Network / Country presentation adapter
// (C1 foundation). Consumed BY the Generate Invoice modal ONLY, as
// PURE PRESENTATION over the existing strict invoice-lane semantics.
// The invoice cohort / LANE_REGISTRY / prefix / suffix contract is
// UNCHANGED — C4 wires canonical (Network, Country) selectors to the
// existing `LANE_TO_QUERY` + `network_lane` payload via the local
// `canonicalPairToLaneId` inverse helper.
import {
  NETWORK_LABELS,
  COUNTRY_LABELS,
  TRANSACTION_CAPABILITY,
  getVisibleCountries,
} from "../../lib/networkCountryCatalog";
// P4-N5B — C4 UI-B R1 (Feb 2026). Customer-scoped Network dropdown
// authorization for `+Generate Invoice`. Byte-parity clone of the
// frozen C7 helper `deriveAuthorizedInvoiceNetworks` — see the
// module header for the extraction rule (frozen C7 stays intact).
import { deriveAuthorizedInvoiceNetworks } from "../../lib/invoiceNetworkAuthorization";

function invoicePdfHref(invoiceId) {
  return backendDownloadUrl(`/invoices/${invoiceId}/pdf`);
}

const downloadInvoicePdf = async (invoiceId) => {
  try {
    await downloadFromBackend(`/invoices/${invoiceId}/pdf`, {});
  } catch (_) { /* download helper toasts on its own */ }
};

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  // Admin-configurable low-margin threshold. Loaded from /api/settings
  // and persisted via PATCH when the operator presses Save. Fallback to
  // 3 if the settings endpoint is unreachable — same default the backend
  // ships in `routes/settings.py::_DEFAULTS`.
  const [thresholdPct, setThresholdPct] = useState(3);
  // Local edit buffer so the value doesn't re-render on every keystroke.
  // We only push to backend (and to `thresholdPct`) on Save.
  const [thresholdInput, setThresholdInput] = useState("3");
  const [savingThreshold, setSavingThreshold] = useState(false);
  // Filter toggle: when true the list is client-side filtered to invoices
  // strictly under the threshold. Cheap because the list is already <500.
  const [lowOnly, setLowOnly] = useState(false);

  // Phase Step-1 (Feb 2026) — three-level admin navigation:
  //   level "customers" → level "months" → level "invoices"
  // Pure UI grouping over the existing `GET /invoices` response. No
  // new backend calculations; every financial field on each invoice
  // row (total, subtotal, tax_amount, paid_amount, gross_profit,
  // margin_pct) is displayed EXACTLY as the API returns it. Download
  // and delete actions are re-used verbatim on the leaf level.
  const [level, setLevel] = useState("customers");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // "YYYY-MM"
  const [customerSearch, setCustomerSearch] = useState("");
  const [invActiveOnly, setInvActiveOnly] = useAdminPref("invoices_active_only");
  // Step 2A — Generate Invoice preview state. Preview-only: no invoice
  // is created, no transaction is mutated. Selection lives entirely in
  // this component's frontend state.
  // Step 2A.1 (Feb 2026) — added `country` to reach parity with the
  // Admin Customer → Transactions tab filter row. Values map 1:1 with
  // the existing `GET /api/transactions?country=` contract:
  //   ""  → Both  (param omitted)
  //   "CA" → Canada only
  //   "US" → USA only
  // Dependency: Pipeline is Canada-only, so switching Network → Pipeline
  // auto-resets country to "CA" (see the Network <select> onChange below).
  const [genFilters, setGenFilters] = useState({
    start: "", end: "", network: "", country: "", card: "", driver: "", unit: "",
  });
  const [genRows, setGenRows] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [genSelected, setGenSelected] = useState(new Set());
  // Step 2B-1 (Feb 2026) — manual generation state.
  // `genConfirmOpen`  → confirmation modal visibility.
  // `genSubmitting`   → single-flight guard against double-clicks / retries.
  // `genResult`       → last success payload (invoice_number, lines, total).
  // `genRejected`     → 409 rejected-id breakdown for the error banner.
  const [genConfirmOpen, setGenConfirmOpen] = useState(false);
  const [genSubmitting, setGenSubmitting] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [genRejected, setGenRejected] = useState(null);

  // PHASE-4-P4-INVOICE-DATE-SELECTION-R1 (2026-02) — operator-selectable
  // invoice metadata dates. Defaults MATCH the backend's pre-R1
  // convenience defaults verbatim: Invoice Date = today (local),
  // Due Date = today + 14 days. Both fields are editable. If the
  // operator leaves them untouched, the generated invoice's persisted
  // dates and the Preview PDF are BIT-IDENTICAL to pre-R1 behaviour.
  // These are invoice METADATA ONLY — they never influence the
  // transaction cohort, pricing, tax, subtotal, total, margin, or GP.
  function _addDaysIso(iso, days) {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + days);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  }
  const _todayIso = () => {
    const d = new Date();
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };
  // P4-INVOICE-NOMENCLATURE-NETWORK-AUTHORITY R1 (Feb 2026) — canonical
  // lane → (persisted-network, country) mapping used to:
  //  (a) translate the operator-visible lane back to the existing
  //      `/transactions/eligible-*` endpoint's (network, country) query
  //      contract, and
  //  (b) forward the raw `network_lane` value in Preview + Generate
  //      body payloads so the backend `LANE_REGISTRY` authoritatively
  //      drives the invoice-number prefix.
  //
  // P4-N5B SLICE C4 R1 (2026-09-19) — the LANE_TO_QUERY dictionary
  // itself is UNCHANGED. It remains the authoritative wire dictionary
  // consumed by `/transactions/eligible-*`. Slice C4 introduces a pure
  // INVERSE helper `canonicalPairToLaneId(network, country)` that lets
  // the modal present canonical (Network, Country) selectors to the
  // operator while still emitting the existing strict `network_lane`
  // string on the wire. **The invoice cohort semantics are byte-
  // identical PRE ≡ POST**: Esso-USA still resolves to
  // `network=ta_petro&country=US` on the eligible-tx query (NOT the
  // C2/C3 widening rule); `LANE_REGISTRY["esso_us"].permitted_networks
  // = {ta_petro}` remains untouched.
  const LANE_TO_QUERY = {
    esso_ca:       { network: "esso",       country: "CA" },
    esso_us:       { network: "ta_petro",   country: "US" },
    petro_pass_ca: { network: "petro_pass", country: "CA" },
    // P4-PP-USA-COUNTRY-SELECTOR R1 (Feb 2026) — additive Petro-Pass USD/GAL
    // lane. Maps to the same persisted-network ("petro_pass") as the CA
    // sibling but with country="US". Country selector is auto-locked to
    // "USA" when this lane is picked (mirroring esso_us / pipeline_ca).
    petro_pass_us: { network: "petro_pass", country: "US" },
    pipeline_ca:   { network: "pipeline",   country: "CA" },
  };
  // Map the Network dropdown value (a canonical lane id or "") to the
  // raw persisted-network filter the backend eligible-tx endpoint
  // expects. Returns null when no filter should be applied ("All").
  const laneToNetworkFilter = (lane) => LANE_TO_QUERY[lane]?.network || "";

  // P4-N5B SLICE C4 R1 — pure inverse of LANE_TO_QUERY (no new business
  // rule, no widening, no supplier reinterpretation). Given the operator's
  // canonical (Network, Country) selection, returns the existing strict
  // `network_lane` id or `null` when either (a) Network is empty
  // ("All networks" path, where backend `_NETWORK_PREFIX_MAP` handles
  // per-row prefix derivation) or (b) the pair is out-of-capability.
  //
  // Preserves the accepted C4 RCA finding: this helper is invoice-
  // specific business logic; it MUST NOT be replaced by C1's
  // `toWireParams`. C1's helper would emit `network=esso&country=US`
  // for Esso-USA, which the invoice `LANE_REGISTRY` refuses (permitted
  // = {ta_petro}).
  const canonicalPairToLaneId = (network, country) => {
    if (!network) return null;                                          // "All networks"
    if (network === "esso"       && country === "CA") return "esso_ca";
    if (network === "esso"       && country === "US") return "esso_us";
    if (network === "pipeline"   && country === "CA") return "pipeline_ca";
    if (network === "petro_pass" && country === "CA") return "petro_pass_ca";
    if (network === "petro_pass" && country === "US") return "petro_pass_us";
    return null;                                                        // out-of-capability
  };

  // Slice C4 — Country options for the invoice Generate modal.
  // Consumes C1 `TRANSACTION_CAPABILITY` directly; DOES NOT duplicate
  // per-network capability rules inside this file. Pipeline is
  // single-capability (CA-only) and locks the Country selector.
  const _INVOICE_ALL_NETWORKS_COUNTRIES = (() => {
    const union = Array.from(
      new Set(Object.values(TRANSACTION_CAPABILITY).flat())
    );
    return union.length > 1 ? ["", ...union] : union;
  })();
  const _invoiceCountriesFor = (network) => {
    if (!network) return _INVOICE_ALL_NETWORKS_COUNTRIES;
    return getVisibleCountries("transactions", network);
  };

  // Slice C4 — Invoice-cohort validity gate. Prevents the operator from
  // submitting a request the backend will reject with `mixed_country_cohort`.
  //   Valid: (specific-network, specific-country) → one of 5 strict lanes
  //   Valid: (All networks, specific-country)     → multi-network single-country
  //   INVALID: (All networks, All country)        → backend rejects
  //   INVALID: (specific-network, All country)    → mixed-country risk on
  //            multi-country providers (esso, petro_pass); pipeline is
  //            CA-locked so this branch never fires for it.
  const isInvoiceCombinationValid = (network, country) => {
    if (network && country) return true;
    if (!network && country) return true;
    return false;
  };

  const [genIssueDate, setGenIssueDate] = useState(_todayIso());
  // P4-INVOICE-DEFAULT-DUE-DATE-NEXT-DAY R1 (2026-02) — operator-facing
  // convenience: prefill Due Date to Invoice Date + 1 calendar day.
  // Metadata only; field remains fully editable. No effect on cohort,
  // pricing, tax, subtotal, total, margin, GP, or invoice numbering.
  const [genDueDate, setGenDueDate] = useState(_addDaysIso(_todayIso(), 1));
  const genDatesInvalid =
    !genIssueDate || !genDueDate || genDueDate < genIssueDate;

  // PHASE-4-P4-INVOICE-V2-SERVICE-CHARGES-DISCOUNT-AND-LABELS R1 (Feb 2026)
  // — additive Invoice Adjustments state. Optional, positional between
  // the Invoice/Due-Date row and the Preview Total block. Blank/zero
  // values are semantically absent — no financial effect. Server-side
  // computes tax (Option A: customer.billing_province via
  // tax.tax_for_province) and persists ONLY when amount > 0. USA
  // invoices force service-charge tax = 0 regardless of `applyTax`.
  const [genServiceChargeDesc, setGenServiceChargeDesc] = useState("");
  const [genServiceChargeAmount, setGenServiceChargeAmount] = useState("");
  const [genServiceChargeApplyTax, setGenServiceChargeApplyTax] = useState(true);
  const [genDiscountDesc, setGenDiscountDesc] = useState("");
  const [genDiscountAmount, setGenDiscountAmount] = useState("");
  // Client-side display map — DISPLAY-ONLY. The actual tax rate/amount
  // is computed authoritatively server-side (never trust this map).
  const CA_TAX_DISPLAY = {
    AB: "GST 5%", BC: "GST+PST 12%", MB: "GST+PST 12%",
    NB: "HST 15%", NL: "HST 15%", NS: "HST 14%", NT: "GST 5%",
    NU: "GST 5%", ON: "HST 13%", PE: "HST 15%",
    QC: "GST+QST 14.98%", SK: "GST+PST 11%", YT: "GST 5%",
  };

  // Step 2C-2A (Feb 2026) — Preview Totals.
  // `previewTotals`   → last successful preview payload {subtotal, tax_amount,
  //                     total, tax_rate, period_start, period_end}. Cleared
  //                     to null whenever selection changes, so a stale total
  //                     can NEVER be shown against a newer selection.
  // `previewedIdsKey` → the exact selection signature that produced
  //                     `previewTotals`. Compared to the current selection
  //                     signature to gate the Generate button.
  // `previewLoading`  → true while a request is in flight (or debounce
  //                     is pending); UI shows "Calculating…".
  // `previewInvalid`  → true iff the server returned rejected>0 for the
  //                     currently-displayed selection. Totals stay null;
  //                     UI tells operator to re-preview / clear rejects.
  // Refs: `previewSeqRef` implements latest-request-wins so an older,
  //       slower response never overwrites a newer selection's totals.
  //       `previewTimerRef` holds the debounce timeout id.
  const [previewTotals, setPreviewTotals] = useState(null);
  const [previewedIdsKey, setPreviewedIdsKey] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewInvalid, setPreviewInvalid] = useState(false);
  const previewSeqRef = useRef(0);
  const previewTimerRef = useRef(null);

  // Per-row hard-delete modal state. Matches the Transactions / Upload
  // History pattern: typed "I UNDERSTAND THIS IS IRREVERSIBLE" prompt
  // before the call goes through. We store the whole invoice row so the
  // modal can render a meaningful impact summary (invoice #, total).
  const [deleteTarget, setDeleteTarget] = useState(null);

  // PHASE-4-P4-INVOICE-FIRST-CUSTOMER-ELIGIBILITY-R1 (2026-02) —
  // Independent customer master state, populated from `GET /customers`.
  // Merged with the invoice-derived aggregate in `customerGroups`
  // below so an admin can start a legitimate customer's FIRST invoice
  // even when that customer has zero prior invoices. The endpoint
  // already excludes `is_test_fixture=True` by default (see
  // `backend/routes/customers.py::list_customers` line 287), so no
  // local safety filter is required — test fixtures never surface
  // here without a client explicitly opting in.
  const [customersMaster, setCustomersMaster] = useState([]);
  const load = async () => {
    const params = tab !== "all" ? { status: tab } : {};
    const [invResp, custResp] = await Promise.all([
      api.get("/invoices", { params }),
      api.get("/customers").catch(() => ({ data: [] })),
    ]);
    setRows(invResp.data);
    setCustomersMaster(Array.isArray(custResp.data) ? custResp.data : []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      if (typeof data?.low_margin_threshold_pct === "number") {
        setThresholdPct(data.low_margin_threshold_pct);
        setThresholdInput(String(data.low_margin_threshold_pct));
      }
    }).catch(() => { /* keep default 3% */ });
  }, []);

  const saveThreshold = async () => {
    const v = Number(thresholdInput);
    if (!Number.isFinite(v) || v < 0 || v > 100) {
      toast.error("Threshold must be between 0 and 100.");
      return;
    }
    setSavingThreshold(true);
    try {
      const { data } = await api.patch("/settings", { low_margin_threshold_pct: v });
      setThresholdPct(data.low_margin_threshold_pct);
      toast.success(`Low-margin threshold set to ${data.low_margin_threshold_pct}%`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save threshold");
    } finally {
      setSavingThreshold(false);
    }
  };

  // Phase 1 lockdown (Feb 2026) — the "Generate from pending" button
  // was retired. The ONLY supported invoice-creation path is the
  // manual Admin workflow: Invoices → Customer → + Generate Invoice
  // → select transactions → Generate Invoice. All local state and
  // handlers that fired the broad `/invoices/generate-async` route
  // have been removed. See `_generate_from_selected_ids` in
  // `backend/routes/invoices.py` for the only surviving path.

  // `margin_pct` only exists when the API was hit as admin — if the field
  // is missing we can't tell whether a row is low-margin, so we treat it
  // as "not low" and keep it visible. Belt-and-braces against accidentally
  // hiding rows from a customer who shouldn't see this filter anyway.
  const isLowMargin = (r) => typeof r.margin_pct === "number" && r.margin_pct < thresholdPct && (r.tx_subtotal || 0) > 0;
  const visibleRows = lowOnly ? rows.filter(isLowMargin) : rows;
  const lowCount = rows.filter(isLowMargin).length;

  // Step 2C-2A — Preview Totals effect. Fires ONLY on the Generate
  // Invoice screen when a selection exists. Structural guarantees:
  //   * zero selection            → no request, totals hidden
  //   * selection change          → totals INSTANTLY invalidated
  //   * new fetch                 → debounced ~300 ms
  //   * concurrent fetches        → latest-request-wins via
  //                                 `previewSeqRef` sequence number;
  //                                 stale responses ignored
  //   * server returns rejected   → totals stay null, `previewInvalid`
  //                                 flips true; UI asks operator to
  //                                 re-preview (all-or-nothing)
  //   * mixed currency            → mirrors existing Generate math;
  //                                 totals still computed and shown
  // The Generate button gates on `previewedIdsKey === selectionKey`
  // so no invoice can be created against a stale preview.
  const selectionKey = React.useMemo(
    () => Array.from(genSelected).sort().join("|"),
    [genSelected],
  );

  useEffect(() => {
    // Instantly invalidate — a stale total may never linger even for
    // one frame after a checkbox flips.
    setPreviewTotals(null);
    setPreviewInvalid(false);
    setPreviewedIdsKey("");
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    if (level !== "generate" || !selectedCustomerId || genSelected.size === 0) {
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    const idsSnapshot = Array.from(genSelected);
    const keySnapshot = selectionKey;
    const seq = ++previewSeqRef.current;
    previewTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/invoices/preview", {
          customer_id: selectedCustomerId,
          transaction_ids: idsSnapshot,
          // PHASE-4-P4-INVOICE-V2-SERVICE-CHARGES-DISCOUNT-AND-LABELS R1
          // — additive adjustments; server echoes back the normalized
          // service_charge / discount / final_total / display_total_tax
          // so the Preview Totals block matches what Generate persists.
          service_charge_description: genServiceChargeDesc || null,
          service_charge_amount: genServiceChargeAmount === "" ? null : Number(genServiceChargeAmount),
          service_charge_apply_tax: !!genServiceChargeApplyTax,
          discount_description: genDiscountDesc || null,
          discount_amount: genDiscountAmount === "" ? null : Number(genDiscountAmount),
          // P4-INVOICE-NOMENCLATURE-NETWORK-AUTHORITY R1 — authoritative
          // NETWORK INTENT. When set, backend LANE_REGISTRY validates
          // cohort subset + country and drives the invoice-number
          // prefix. When null, backend country-wise fallback applies.
          network_lane: canonicalPairToLaneId(genFilters.network, genFilters.country),
        });
        // Latest-request-wins. If the operator flipped a checkbox
        // between fire and land, drop this response silently.
        if (seq !== previewSeqRef.current) return;
        if ((data.rejected_count || 0) > 0) {
          // All-or-nothing: totals invalidated.
          setPreviewInvalid(true);
          setPreviewTotals(null);
          setPreviewedIdsKey("");
        } else {
          setPreviewInvalid(false);
          setPreviewTotals({
            subtotal: data.subtotal,
            tax_rate: data.tax_rate,
            tax_amount: data.tax_amount,
            total: data.total,
            tax_breakdown: data.tax_breakdown,
            period_start: data.period_start,
            period_end: data.period_end,
            accepted_count: data.accepted_count,
            // PHASE 4 P3C (2026-09-11) — additive customer enrichment
            // consumed by `openInvoicePreview` to render a draft-invoice
            // style preview. Read-only pass-through from the backend
            // /api/invoices/preview response; no arithmetic.
            customer_name: data.customer_name,
            billing_address: data.billing_address,
            billing_email: data.billing_email,
            // PHASE 4 P3D4 — Bill-To completeness
            billing_province: data.billing_province,
            billing_phone: data.billing_phone,
            // PHASE 4 P3D5 — structured postal-address pass-through so
            // Page-1 BILL TO can render a conventional multi-line address.
            billing_city: data.billing_city,
            billing_postal_code: data.billing_postal_code,
            billing_country: data.billing_country,
            // PHASE 4 P3D (2026-09-11) — multi-page Preview payload.
            // Product/Province rollup + full transaction-report rows
            // (from the frozen `_build_transactions_report` helper).
            product_summary: data.product_summary,
            report_rows: data.report_rows,
            report_columns: data.report_columns,
            currency: data.currency,
            // P4-INVOICE-V2-SERVICE-CHARGES-DISCOUNT-AND-LABELS R1 —
            // adjustments echoed by server. `final_total` and
            // `display_total_tax` are what Generate will persist.
            service_charge: data.service_charge,
            discount: data.discount,
            final_total: data.final_total,
            display_total_tax: data.display_total_tax,
            invoice_feature_version: data.invoice_feature_version,
            // P4-INVOICE-NOMENCLATURE-NETWORK-AUTHORITY R1 — lane echo
            // + provisional invoice-number prefix (no suffix consumed).
            network_lane: data.network_lane,
            provisional_invoice_number_prefix: data.provisional_invoice_number_prefix,
          });
          setPreviewedIdsKey(keySnapshot);
        }
      } catch (e) {
        if (seq !== previewSeqRef.current) return;
        setPreviewTotals(null);
        setPreviewInvalid(false);
        setPreviewedIdsKey("");
        // Don't toast on preview errors — noisy on every keystroke.
        // Generate button will remain disabled which is the visible tell.
      } finally {
        if (seq === previewSeqRef.current) setPreviewLoading(false);
      }
    }, 300);
    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey, selectedCustomerId, level, genServiceChargeAmount, genServiceChargeApplyTax, genDiscountAmount, genServiceChargeDesc, genDiscountDesc]);

  const counts = rows.reduce((a, r) => {
    a[r.status] = (a[r.status] || 0) + 1; return a;
  }, {});

  // Group rows by customer for level 1. Every value comes straight from
  // the invoice records — no derived totals are computed here.
  const customerGroups = React.useMemo(() => {
    const map = new Map();
    // Base layer: every legitimate customer from the customer master
    // (P4-INVOICE-FIRST-CUSTOMER-ELIGIBILITY R1) — includes zero-invoice
    // customers so admin can start their FIRST invoice. Test fixtures
    // are already filtered out server-side by GET /customers.
    for (const c of customersMaster) {
      const cid = c.customer_id || "";
      if (!cid) continue;
      map.set(cid, {
        customer_id: cid,
        customer_name: c.company_name || "",
        customer_status: c.status || null,
        billing_days: c.billing_days || [],
        invoice_count: 0,
        latest_issue_date: null,
        latest_invoice_number: null,
      });
    }
    // Overlay: aggregate invoice history over the same customer_id
    // (dedup by customer_id). Legacy invoice rows whose customer has
    // been removed from the master still surface here so operators
    // never lose visibility of historical work.
    for (const r of rows) {
      const cid = r.customer_id || "";
      const name = r.customer_name || "";
      const b = map.get(cid) || {
        customer_id: cid,
        customer_name: name,
        invoice_count: 0,
        latest_issue_date: null,
        latest_invoice_number: null,
      };
      // Prefer master-provided company_name when both sources have one;
      // fall back to invoice-embedded name (legacy invoices, orphaned
      // customers) so the row is never nameless.
      if (!b.customer_name && name) b.customer_name = name;
      b.invoice_count += 1;
      if (!b.latest_issue_date || (r.issue_date || "") > b.latest_issue_date) {
        b.latest_issue_date = r.issue_date || null;
        b.latest_invoice_number = r.invoice_number || null;
      }
      map.set(cid, b);
    }
    return Array.from(map.values()).sort(
      (a, b) => (a.customer_name || "").localeCompare(b.customer_name || ""),
    );
  }, [rows, customersMaster]);

  const filteredCustomers = React.useMemo(() => {
    const needle = customerSearch.trim().toLowerCase();
    const base = invActiveOnly ? customerGroups.filter((c) => c.customer_status === "active") : customerGroups;
    if (!needle) return base;
    return base.filter(
      (c) => (c.customer_name || "").toLowerCase().includes(needle),
    );
  }, [customerGroups, customerSearch, invActiveOnly]);

  // Level 2: months for the selected customer. Grouping key is the
  // authoritative `period_start` when present, else falls back to
  // `issue_date`. This mirrors how the existing flat list already
  // renders those fields — nothing new is computed.
  const monthGroups = React.useMemo(() => {
    if (!selectedCustomerId) return [];
    const map = new Map();
    for (const r of rows) {
      if (r.customer_id !== selectedCustomerId) continue;
      const src = r.period_start || r.issue_date || "";
      const monthKey = src.slice(0, 7); // "YYYY-MM"
      if (!monthKey) continue;
      const b = map.get(monthKey) || {
        month_key: monthKey,
        invoice_count: 0,
        latest_issue_date: null,
      };
      b.invoice_count += 1;
      if (!b.latest_issue_date || (r.issue_date || "") > b.latest_issue_date) {
        b.latest_issue_date = r.issue_date || null;
      }
      map.set(monthKey, b);
    }
    return Array.from(map.values()).sort(
      (a, b) => b.month_key.localeCompare(a.month_key),
    );
  }, [rows, selectedCustomerId]);

  // Level 3: individual invoices restricted to (customer, month).
  const leafRows = React.useMemo(() => {
    if (!selectedCustomerId || !selectedMonth) return [];
    return visibleRows.filter((r) => {
      if (r.customer_id !== selectedCustomerId) return false;
      const src = r.period_start || r.issue_date || "";
      return src.slice(0, 7) === selectedMonth;
    });
  }, [visibleRows, selectedCustomerId, selectedMonth]);

  const selectedCustomerName = customerGroups.find(
    (c) => c.customer_id === selectedCustomerId,
  )?.customer_name || selectedCustomerId || "";

  // P4-N5B C4 UI-B R1 — customer-scoped Network dropdown source.
  // `customersMaster` is already populated by `GET /customers` at
  // component load (line ~271) and its `{"_id": 0}` server-side
  // projection includes `price_report_networks` + `authorized_lanes`
  // for every customer. Reuses the same `.find(c => ...)` pattern
  // as the display-name lookup directly above — no new endpoint
  // and no additional network request.
  const selectedCustomerProfile = React.useMemo(
    () => customersMaster.find((c) => c.customer_id === selectedCustomerId) || null,
    [customersMaster, selectedCustomerId],
  );
  const authorizedNetworks = React.useMemo(
    () => deriveAuthorizedInvoiceNetworks(selectedCustomerProfile),
    [selectedCustomerProfile],
  );

  // Stale-selection reconciler — mirrors the frozen C7 pattern at
  // `CustomerInvoices.jsx :: useEffect(() => {...}, [authorizedNetworks, network])`.
  // If the operator picked a specific network and then switched
  // customers (or the profile refreshed), and that network is no
  // longer authorized, reset to "" (All networks). The existing
  // Network → Country reconciler will subsequently rebuild a valid
  // country default via `_invoiceCountriesFor("")`. Never touches a
  // still-authorized selection, so no render-loop hazard.
  useEffect(() => {
    if (genFilters.network && !authorizedNetworks.includes(genFilters.network)) {
      setGenFilters((gf) => ({ ...gf, network: "" }));
    }
  }, [authorizedNetworks, genFilters.network]);

  function monthLabel(m) {
    if (!m || m.length !== 7) return m || "—";
    const [y, mm] = m.split("-");
    const d = new Date(Number(y), Number(mm) - 1, 1);
    return d.toLocaleString(undefined, { month: "long", year: "numeric" });
  }

  return (
    <SectionPad>
      <PageHeader
        title="Invoices"
        subtitle="BILLING / INVOICES"
        testId="invoices-header"
      />

      {/* Admin-configurable margin threshold + low-margin filter. */}
      <div className="gf-card gf-card-body mb-4 flex flex-wrap items-end gap-4" data-testid="margin-controls">
        <div>
          <div className="overline mb-1 flex items-center gap-1"><Gear size={12}/> LOW-MARGIN THRESHOLD %</div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              className="gf-input w-24"
              value={thresholdInput}
              onChange={(e) => setThresholdInput(e.target.value)}
              data-testid="low-margin-threshold-input"
            />
            <button
              className="gf-btn gf-btn-outline"
              onClick={saveThreshold}
              disabled={savingThreshold || thresholdInput === String(thresholdPct)}
              data-testid="low-margin-threshold-save"
            >
              <FloppyDisk size={14}/> {savingThreshold ? "Saving…" : "Save"}
            </button>
            <span className="text-xs text-zinc-500">
              Current: <b className="font-mono">{thresholdPct}%</b>. Invoices under this margin are highlighted red.
            </span>
          </div>
        </div>
        <div className="ml-auto">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={lowOnly}
              onChange={(e) => setLowOnly(e.target.checked)}
              data-testid="filter-low-margin-only"
            />
            <span>Show only low-margin invoices</span>
            <Badge variant={lowCount > 0 ? "warning" : "info"}>{lowCount} in view</Badge>
          </label>
        </div>
      </div>

      <div className="flex gap-1 mb-4" data-testid="invoice-tabs">
        {["all", "issued", "sent", "partially_paid", "paid", "overdue"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`inv-tab-${t}`}
            className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border ${tab === t ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}
          >
            {t.replace("_", " ")} {t !== "all" && `(${counts[t] || 0})`}
          </button>
        ))}
      </div>

      {/* Breadcrumb — three-level nav (Step 1). Pure UI. */}
      <div className="mb-4 text-sm font-mono flex items-center gap-2 flex-wrap" data-testid="invoices-breadcrumb">
        <button
          className={level === "customers" ? "font-semibold underline" : "hover:underline"}
          onClick={() => { setLevel("customers"); setSelectedCustomerId(null); setSelectedMonth(null); }}
          data-testid="crumb-customers"
        >
          Invoices
        </button>
        {selectedCustomerId && (
          <>
            <span className="text-zinc-400">›</span>
            <button
              className={level === "months" ? "font-semibold underline" : "hover:underline"}
              onClick={() => { setLevel("months"); setSelectedMonth(null); }}
              data-testid="crumb-customer"
            >
              {selectedCustomerName}
            </button>
          </>
        )}
        {selectedMonth && (
          <>
            <span className="text-zinc-400">›</span>
            <span className="font-semibold" data-testid="crumb-month">{monthLabel(selectedMonth)}</span>
          </>
        )}
      </div>

      {level === "months" && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <button
            className="gf-btn gf-btn-outline gf-btn-sm"
            onClick={() => { setLevel("customers"); setSelectedCustomerId(null); setSelectedMonth(null); }}
            data-testid="invoices-back-to-customers"
          >
            ← Back to Customers
          </button>
          <button
            className="gf-btn gf-btn-primary gf-btn-sm ml-auto"
            onClick={() => { setLevel("generate"); setGenRows([]); setGenSelected(new Set()); }}
            data-testid="invoices-open-generate"
            title="Preview transactions eligible for a new invoice. No invoice is generated in this step."
          >
            + Generate Invoice
          </button>
        </div>
      )}

      {level === "invoices" && (
        <div className="mb-3">
          <button
            className="gf-btn gf-btn-outline gf-btn-sm"
            onClick={() => { setLevel("months"); setSelectedMonth(null); }}
            data-testid="invoices-back-to-months"
          >
            ← Back to Months
          </button>
        </div>
      )}

      {level === "generate" && (() => {
        // Step 2A — read-only preview. Reuses the exact `GET /api/transactions`
        // endpoint the Admin Customer → Transactions tab already uses.
        // Eligibility mirrors `services/invoice_service.run_invoice_generate`
        // (status ∈ {pending, flagged} AND invoice_id IS NULL). We DO NOT
        // recompute financials — every field displayed is the value the
        // API already returns.
        const eligible = (r) => (r.status === "pending" || r.status === "flagged")
                              && !r.invoice_id;
        const totalRows = genRows.length;
        const eligibleRows = genRows.filter(eligible);
        const excludedCount = totalRows - eligibleRows.length;

        // Step 2C-1 — build a fast lookup so the preview table can flag
        // rows that the backend just rejected in the last 409 response.
        // The map is authoritative for the current 409 only; a fresh
        // preview refresh will populate `genRows` with the up-to-date
        // status (already-invoiced etc.) and this map is cleared.
        const rejectedById = new Map(
          (genRejected?.rejected || []).map((r) => [r.transaction_id, r.reason])
        );
        function friendlyReason(reason) {
          if (!reason) return "No longer eligible";
          if (reason === "already_invoiced")   return "Already invoiced";
          if (reason === "soft_deleted")       return "Deleted";
          if (reason === "wrong_customer")     return "Belongs to another customer";
          if (reason === "not_found")          return "No longer exists";
          if (reason.startsWith("ineligible_status:")) return "No longer eligible";
          return "No longer eligible";
        }

        async function runPreview() {
          if (!selectedCustomerId) return;
          setGenLoading(true); setGenSelected(new Set());
          try {
            // P4-N5B C4 PHASE 2A (Feb 2026) — atomic invoice-preflight
            // filter-integrity fix. Prior to this fix, when the
            // operator picked (Network=Esso|Petro-Pass, Country=All)
            // `canonicalPairToLaneId` returned null and the network
            // URL parameter was silently dropped, causing the Preview
            // to leak transactions from other networks (Esso+All
            // returned Petro-Pass rows; Petro-Pass+All returned Esso
            // rows). The invoice cohort semantics themselves are
            // unchanged — this fix only corrects the frontend request
            // builder so Preview eligibility honors the operator's
            // network choice. Country-specific lanes (5 strict
            // LANE_TO_QUERY entries) and the All-networks fallback
            // are UNTOUCHED.
            //
            // Base params: every filter EXCEPT network/country. These
            // are appended per-branch below so the Esso+All dual call
            // sends byte-identical filters on both requests.
            const baseParams = () => {
              const p = new URLSearchParams({ customer_id: selectedCustomerId, limit: "2000" });
              if (genFilters.start)  p.set("start",  genFilters.start);
              if (genFilters.end)    p.set("end",    genFilters.end);
              if (genFilters.card)   p.set("card",   genFilters.card);
              if (genFilters.driver) p.set("driver", genFilters.driver);
              if (genFilters.unit)   p.set("unit",   genFilters.unit);
              return p;
            };
            // Response shape has always been either a bare array or
            // `{rows:[...]}`; preserved verbatim from the pre-fix
            // extraction so pagination/limit semantics are unchanged.
            const extractRows = (data) => (Array.isArray(data) ? data : (data?.rows || []));

            const laneId = canonicalPairToLaneId(genFilters.network, genFilters.country);
            let merged;
            if (genFilters.network === "esso" && genFilters.country === "US") {
              // P4 LISSET ESSO-USA COUNTRY-FILTER INCIDENT · R2 OPTION A
              // (Feb 2026). Historical Esso-US compat boundary: valid
              // production rows exist under BOTH persisted shapes:
              //   LEGACY    : network="esso"     / country="US"
              //   CANONICAL : network="ta_petro" / country="US"
              // Backend widening (transactions.py:4465) fires only when
              // the URL literal is "esso", so the pre-fix
              // LANE_TO_QUERY["esso_us"] single-request path
              // (`network=ta_petro&country=US`) hid the 51 legacy-shape
              // rows from the picker. The compat boundary is honored on
              // every other production surface (portal, reports, GP,
              // dedup, card matcher, invoice-number lane) — this branch
              // brings the Manual-Invoice picker into parity by mirroring
              // the existing Esso+All dual-fetch/dedupe pattern below.
              //
              // Contract:
              //   * Two failure-atomic parallel requests, identical
              //     customer/date/card/driver/unit constraints.
              //   * Merge deduped strictly by `transaction_id` (never
              //     card/station/date/amount/product).
              //   * network_lane for POST /invoices/preview is emitted
              //     elsewhere via canonicalPairToLaneId and continues to
              //     resolve to "esso_us" — invoice-number lane semantics
              //     unchanged.
              //   * Country=CA/All branches, other networks, and all
              //     eligibility rules are unchanged.
              const pA = baseParams(); pA.set("network", "esso");     pA.set("country", "US");
              const pB = baseParams(); pB.set("network", "ta_petro"); pB.set("country", "US");
              const [rA, rB] = await Promise.all([
                api.get(`/transactions?${pA.toString()}`),
                api.get(`/transactions?${pB.toString()}`),
              ]);
              const rowsA = extractRows(rA.data);
              const rowsB = extractRows(rB.data);
              // Deterministic ordering: legacy `esso` leg first, canonical
              // `ta_petro` leg second (mirrors the Esso+All merge order).
              const seen = new Set();
              merged = [];
              for (const row of rowsA.concat(rowsB)) {
                const tid = row && row.transaction_id;
                if (!tid || seen.has(tid)) continue;
                seen.add(tid);
                merged.push(row);
              }
            } else if (laneId) {
              // Existing positive path — country-specific lane. Emits
              // the strict (network, country) pair via LANE_TO_QUERY.
              // (Esso+US is now handled by the compat branch above;
              // LANE_TO_QUERY["esso_us"] remains authoritative for the
              // `network_lane` wire value consumed by /invoices/preview
              // via canonicalPairToLaneId — the invoice LANE_REGISTRY
              // contract is unchanged.)
              const p = baseParams();
              const q = LANE_TO_QUERY[laneId];
              if (q?.network) p.set("network", q.network);
              if (q?.country) p.set("country", q.country);
              const r = await api.get(`/transactions?${p.toString()}`);
              merged = extractRows(r.data);
            } else if (genFilters.network === "esso" && !genFilters.country) {
              // Phase 2A — Esso + All Country. Persisted Esso identity
              // is dual (`esso` for CA + `ta_petro` for US). Issue TWO
              // otherwise-identical requests under Promise.all so the
              // call is failure-atomic: if either branch rejects, the
              // exception propagates to the outer catch and
              // setGenRows is NEVER invoked with a partial cohort.
              const pA = baseParams(); pA.set("network", "esso");
              const pB = baseParams(); pB.set("network", "ta_petro");
              const [rA, rB] = await Promise.all([
                api.get(`/transactions?${pA.toString()}`),
                api.get(`/transactions?${pB.toString()}`),
              ]);
              const rowsA = extractRows(rA.data);
              const rowsB = extractRows(rB.data);
              // Merge + dedupe by authoritative `transaction_id`
              // (NEVER by card / station / merchant / date / amount).
              // Deterministic ordering: esso branch first, ta_petro
              // branch second; no arbitrary reordering.
              const seen = new Set();
              merged = [];
              for (const row of rowsA.concat(rowsB)) {
                const tid = row && row.transaction_id;
                if (!tid || seen.has(tid)) continue;
                seen.add(tid);
                merged.push(row);
              }
            } else if (genFilters.network === "petro_pass" && !genFilters.country) {
              // Phase 2A — Petro-Pass + All Country. No identity
              // duality; single request. Country deliberately omitted
              // so the backend returns Petro-Pass rows across all its
              // supported countries. Cross-network leaks (esso /
              // ta_petro / pipeline) are excluded by the explicit
              // `network=petro_pass` predicate.
              const p = baseParams();
              p.set("network", "petro_pass");
              const r = await api.get(`/transactions?${p.toString()}`);
              merged = extractRows(r.data);
            } else if (!genFilters.network && genFilters.country) {
              // Existing All-networks + specific-country fallback.
              const p = baseParams();
              p.set("country", genFilters.country);
              const r = await api.get(`/transactions?${p.toString()}`);
              merged = extractRows(r.data);
            } else {
              // All networks + All country (and any other combination
              // not covered above — Pipeline is country-locked so
              // never reaches this branch in the UI).
              const p = baseParams();
              const r = await api.get(`/transactions?${p.toString()}`);
              merged = extractRows(r.data);
            }
            setGenRows(merged);
          } catch (e) {
            toast.error(`Preview failed: ${e.message || "error"}`);
          } finally { setGenLoading(false); }
        }

        function toggleRow(tid) {
          setGenSelected((prev) => {
            const n = new Set(prev);
            if (n.has(tid)) n.delete(tid); else n.add(tid);
            return n;
          });
        }
        function selectAllEligible() {
          const n = new Set();
          eligibleRows.forEach((r) => n.add(r.transaction_id));
          setGenSelected(n);
        }
        function clearSelection() { setGenSelected(new Set()); }

        // Step 2C-1 — helpers shared by both preflight exports.
        // Both paths pull EXACTLY the same source dataset:
        //   genRows.filter(tx => genSelected.has(tx.transaction_id))
        // so Print and CSV always represent the same transactions.
        function _selectedTxsForPreflight() {
          return genRows.filter((r) => genSelected.has(r.transaction_id));
        }
        function _preflightFiltersSummary() {
          return {
            "Date From":   genFilters.start   || "",
            "Date To":     genFilters.end     || "",
            "Network":     genFilters.network ? (NETWORK_LABELS[genFilters.network] || genFilters.network) : "All",
            "Country":     COUNTRY_LABELS[genFilters.country] || (genFilters.network === "pipeline" ? "Canada" : "All"),
            "Card #":      genFilters.card    || "",
            "Driver Name": genFilters.driver  || "",
            "Unit #":      genFilters.unit    || "",
          };
        }
        function _sanitizeFilename(s) {
          return String(s || "customer")
            .replace(/[^\w\s.-]+/g, "")
            .replace(/\s+/g, "_")
            .slice(0, 60) || "customer";
        }

        async function openInvoicePreview() {
          // PHASE-4-P3D8 (2026-02) — Mixed-orientation server-composed
          // Preview PDF. Page 1 is Letter portrait (invoice summary,
          // preserved from P3D2-P3D7A); Page 2 onward is Letter
          // landscape rendered by the established
          // `backend.reports.render_report_pdf`. Merged server-side
          // with pypdf so each page keeps its own MediaBox/rotation.
          //
          // Backend endpoint is READ-ONLY. It reuses the SAME
          // `_price_selected_ids` + `_build_transactions_report`
          // helpers as the summary /invoices/preview call — zero new
          // arithmetic, zero writes, zero invoice creation.
          //
          // Retired: the entire browser HTML Page-2+ transaction table
          // (P3D3–P3D7 CSS width tuning). The renderer's built-in
          // auto-widths + font cascade replace it.
          const txs = _selectedTxsForPreflight();
          if (txs.length === 0) return;
          const totals = previewTotals;
          if (!totals || previewInvalid || previewedIdsKey !== selectionKey) {
            toast.error("Preview totals are stale. Wait for the calculating message to clear and try again.");
            return;
          }
          const idsSnapshot = txs.map((t) => t.transaction_id).filter(Boolean);
          if (idsSnapshot.length === 0) {
            toast.error("No selected transactions to preview.");
            return;
          }
          let objectUrl = null;
          let downloadFilename = "invoice-preview-DRAFT.pdf";
          try {
            const resp = await api.post(
              "/invoices/preview/report.pdf",
              {
                customer_id: selectedCustomerId,
                transaction_ids: idsSnapshot,
                // PHASE-4-P4-INVOICE-DATE-SELECTION-R1 — send the operator's
                // chosen invoice/due dates so Preview reflects what
                // Generate will persist.
                issue_date: genIssueDate,
                due_date: genDueDate,
                // PHASE-4-P4-INVOICE-PERIOD-FILTER-BOUNDARY-R1 —
                // send the operator's filter FROM/TO verbatim so the
                // Preview PDF's Invoice Period line matches the visible
                // billing window even when transactions do not span
                // its boundaries. Cohort logic is unaffected.
                filter_from: genFilters.start || null,
                filter_to: genFilters.end || null,
                // P4-INVOICE-V2-SERVICE-CHARGES-DISCOUNT-AND-LABELS R1
                // — optional adjustments (blank/zero = absent).
                service_charge_description: genServiceChargeDesc || null,
                service_charge_amount: genServiceChargeAmount === "" ? null : Number(genServiceChargeAmount),
                service_charge_apply_tax: !!genServiceChargeApplyTax,
                discount_description: genDiscountDesc || null,
                discount_amount: genDiscountAmount === "" ? null : Number(genDiscountAmount),
                // P4-INVOICE-NOMENCLATURE-NETWORK-AUTHORITY R1 —
                // authoritative NETWORK INTENT (see Preview call).
                network_lane: canonicalPairToLaneId(genFilters.network, genFilters.country),
              },
              { responseType: "blob" },
            );
            // PHASE-4-P3D8E (2026-02) — Prefer the server-supplied
            // Content-Disposition filename verbatim. It is already
            // customer/period-specific ("invoice-preview-DRAFT-
            // <CUSTOMER>-<from>_to_<to>.pdf") and safely sanitised
            // by the backend.
            const cd = (resp.headers && (resp.headers["content-disposition"] || resp.headers["Content-Disposition"])) || "";
            const m = /filename\*?=(?:UTF-\d''|)"?([^";]+)"?/i.exec(cd);
            if (m && m[1]) {
              try { downloadFilename = decodeURIComponent(m[1]); }
              catch (_) { downloadFilename = m[1]; }
            } else if (selectedCustomerName) {
              downloadFilename = `Draft Invoice - ${_sanitizeFilename(selectedCustomerName)}.pdf`;
            }
            const blob = new Blob([resp.data], { type: "application/pdf" });
            objectUrl = URL.createObjectURL(blob);
          } catch (e) {
            const msg = (e && e.response && e.response.data && e.response.data.detail && e.response.data.detail.message)
              || (e && e.message)
              || "Preview PDF generation failed.";
            toast.error(String(msg));
            return;
          }
          // PHASE-4-P3D8E (2026-02) — Chromium/Edge silently drop
          // `window.open(blob:…, "_blank")` for application/pdf
          // blobs; the popup stays at `about:blank`. Use the same
          // programmatic anchor-download pattern the CSV export
          // uses below — Edge then opens the downloaded PDF in its
          // native viewer (with the full toolbar) if the operator
          // clicks the download bar, matching the standard
          // "download from a webapp" UX.
          const a = document.createElement("a");
          a.href = objectUrl;
          a.download = downloadFilename;
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          // Free the object URL after the browser had time to
          // consume the download. 60s is generous for any real
          // network.
          setTimeout(() => {
            try { URL.revokeObjectURL(objectUrl); } catch (_) {}
          }, 60_000);
        }

        function downloadPreflightCsv() {
          const txs = _selectedTxsForPreflight();
          if (txs.length === 0) return;
          const header = [
            "Transaction Date", "Transaction Time", "Network", "Country",
            "Card #", "Driver Name", "Unit #", "Station Name", "City",
            "Province / State", "Product", "Quantity", "UOM", "Currency",
            "Status", "Transaction ID",
          ];
          // RFC 4180-ish escape: wrap every value in quotes and double
          // any embedded quote. Handles commas, quotes and line breaks.
          const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
          const row = (r) => [
            r.tran_date || r.transaction_date, r.tran_time || r.transaction_time || "",
            r.network, r.country, r.card_number, r.driver_name, r.unit,
            r.station_name || r.station_id || "", r.city,
            r.province || r.state || "",
            r.product || r.product_canonical || "",
            r.quantity ?? r.qty ?? "", r.uom, r.currency, r.status,
            r.transaction_id,
          ].map(esc).join(",");
          const body = [header.map(esc).join(",")].concat(txs.map(row)).join("\r\n");
          // UTF-8 BOM so Excel opens accented text correctly.
          const blob = new Blob(["\ufeff" + body], { type: "text/csv;charset=utf-8" });
          const stamp = new Date().toISOString().slice(0, 10);
          const fname = `invoice-preflight-${_sanitizeFilename(selectedCustomerName)}-${stamp}.csv`;
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = fname; a.style.display = "none";
          document.body.appendChild(a); a.click();
          setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
          toast.success(`Preflight CSV downloaded (${txs.length} transaction${txs.length === 1 ? "" : "s"})`);
        }

        // Step 2B-1 — manual invoice generation.
        // Sends the exactly-selected transaction ids to the existing
        // POST /invoices/generate (now additive body-mode). Backend is
        // ALL-OR-NOTHING: any ineligible id → 409 with a rejected list,
        // no invoice created, no transaction mutated.
        async function submitGenerate() {
          if (genSubmitting) return;
          const ids = Array.from(genSelected);
          if (ids.length === 0 || !selectedCustomerId) return;
          setGenSubmitting(true);
          setGenRejected(null);
          try {
            const r = await api.post("/invoices/generate", {
              customer_id: selectedCustomerId,
              transaction_ids: ids,
              // PHASE-4-P4-INVOICE-DATE-SELECTION-R1 — operator-selected
              // invoice/due dates. Backend defaults to today/today+14d
              // when omitted; sending them explicitly locks Preview →
              // Generate parity.
              issue_date: genIssueDate,
              due_date: genDueDate,
              // PHASE-4-P4-INVOICE-PERIOD-FILTER-BOUNDARY-R1 — the
              // exact same FROM/TO Preview received. Backend persists
              // these verbatim as invoices.period_start / period_end
              // (overriding min/max derivation). Cohort unchanged.
              filter_from: genFilters.start || null,
              filter_to: genFilters.end || null,
              // P4-INVOICE-V2-SERVICE-CHARGES-DISCOUNT-AND-LABELS R1
              // — additive adjustments. Blank/zero = absent = no
              // financial effect. Server persists finalized values.
              service_charge_description: genServiceChargeDesc || null,
              service_charge_amount: genServiceChargeAmount === "" ? null : Number(genServiceChargeAmount),
              service_charge_apply_tax: !!genServiceChargeApplyTax,
              discount_description: genDiscountDesc || null,
              discount_amount: genDiscountAmount === "" ? null : Number(genDiscountAmount),
              // P4-INVOICE-NOMENCLATURE-NETWORK-AUTHORITY R1 —
              // authoritative NETWORK INTENT (see Preview call).
              network_lane: canonicalPairToLaneId(genFilters.network, genFilters.country),
            });
            const inv = (r.data.invoices || [])[0];
            if (!inv) {
              toast.error("Invoice was not created. Please try again.");
              setGenSubmitting(false);
              return;
            }
            setGenResult({
              invoice_id: inv.invoice_id,
              invoice_number: inv.invoice_number,
              customer_name: selectedCustomerName,
              lines: inv.lines,
              total: inv.total,
              issue_date: inv.issue_date,
              period_start: inv.period_start,
              period_end: inv.period_end,
            });
            setGenConfirmOpen(false);
            toast.success(`Invoice ${inv.invoice_number} generated · ${inv.lines} transaction(s)`);
            // Refresh authoritative backend state — the just-invoiced
            // rows must render as "Already Invoiced" on next preview.
            await runPreview();
            // Also refresh the top-level invoice list so the new row
            // appears when the operator navigates back.
            load();
          } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail;
            if (status === 409 && detail && typeof detail === "object" && Array.isArray(detail.rejected)) {
              // All-or-nothing abort. Show the per-id breakdown so the
              // admin can un-tick the offending rows and retry.
              setGenRejected({
                message: detail.message || "One or more selected transactions are no longer eligible.",
                rejected: detail.rejected,
              });
              // Step 2C-1 — remove ONLY the rejected ids from selection.
              // `runPreview()` (called next) clears the selection Set at
              // the top of its own body, so we compute the "keep" set
              // first and restore it after the refresh, filtered by the
              // authoritative eligibility of the newly-fetched rows.
              const rejIds = new Set(detail.rejected.map((r) => r.transaction_id));
              const keepIds = new Set([...genSelected].filter((id) => !rejIds.has(id)));
              toast.error("Invoice not generated — some selected transactions are no longer eligible.");
              // Refresh preview so the offending rows now show the
              // authoritative backend status (already-invoiced / deleted).
              await runPreview();
              // Restore the non-rejected selections that are still
              // eligible in the freshly-loaded rows.
              setGenRows((rows) => {
                const eligibleNow = new Set(
                  rows.filter((r) => (r.status === "pending" || r.status === "flagged") && !r.invoice_id)
                      .map((r) => r.transaction_id)
                );
                const restored = new Set(
                  [...keepIds].filter((id) => eligibleNow.has(id))
                );
                setGenSelected(restored);
                return rows;
              });
            } else if (status === 400) {
              toast.error(typeof detail === "string" ? detail : "Invalid request.");
            } else {
              toast.error("Invoice could not be generated. Please review the selected transactions and try again.");
            }
          } finally {
            setGenSubmitting(false);
          }
        }

        return (
          <div className="gf-card" data-testid="invoices-level-generate">
            <div className="mb-3">
              <button
                className="gf-btn gf-btn-outline gf-btn-sm"
                onClick={() => { setLevel("months"); }}
                data-testid="invoices-back-to-customer-invoices"
              >
                ← Back to Customer Invoices
              </button>
            </div>
            <div className="gf-card-body space-y-4">
              <div className="text-sm">
                <div className="text-zinc-500 font-mono">Customer</div>
                <div className="font-semibold" data-testid="gen-locked-customer">{selectedCustomerName}</div>
                <div className="font-mono text-xs text-zinc-500">{selectedCustomerId}</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                <label className="text-xs">Date From
                  <input type="date" className="gf-input w-full mt-1"
                    value={genFilters.start} onChange={(e) => setGenFilters({...genFilters, start: e.target.value})}
                    data-testid="gen-filter-start" />
                </label>
                <label className="text-xs">Date To
                  <input type="date" className="gf-input w-full mt-1"
                    value={genFilters.end} onChange={(e) => setGenFilters({...genFilters, end: e.target.value})}
                    data-testid="gen-filter-end" />
                </label>
                <label className="text-xs">Network
                  <select className="gf-input w-full mt-1" value={genFilters.network}
                    onChange={(e) => {
                      // P4-N5B SLICE C4 R1 — canonical Network dropdown.
                      // Values are canonical providers ({"", "esso",
                      // "pipeline", "petro_pass"}) — NOT compound lane
                      // ids. Country is reconciled via C1's capability
                      // helper. The strict `network_lane` wire value
                      // is derived at submit time via the local
                      // `canonicalPairToLaneId` inverse helper.
                      const nw = e.target.value;
                      const validCountries = _invoiceCountriesFor(nw);
                      const nextCountry = validCountries.includes(genFilters.country)
                        ? genFilters.country
                        : (validCountries.length === 1 ? validCountries[0] : genFilters.country);
                      setGenFilters({...genFilters, network: nw, country: nextCountry});
                    }}
                    data-testid="gen-filter-network">
                    {/* P4-N5B C4 UI-B R1 — customer-scoped Network
                        dropdown. "All networks" is unconditional
                        and always FIRST. Specific-network options
                        render only when the selected customer's
                        derived `authorizedNetworks` (union of
                        `price_report_networks` + `authorized_lanes`,
                        canonicalized + deduped) includes that
                        canonical network. Canonical order is
                        preserved via three explicit conditionals
                        (esso → pipeline → petro_pass) so the option
                        ordering never depends on Set iteration
                        order or on the shape of the customer
                        authorization arrays. */}
                    <option value="">All networks</option>
                    {authorizedNetworks.includes("esso")       && <option value="esso" data-testid="gen-filter-network-opt-esso">Esso</option>}
                    {authorizedNetworks.includes("pipeline")   && <option value="pipeline" data-testid="gen-filter-network-opt-pipeline">Pipeline</option>}
                    {authorizedNetworks.includes("petro_pass") && <option value="petro_pass" data-testid="gen-filter-network-opt-petro-pass">Petro-Pass</option>}
                  </select>
                </label>
                <label className="text-xs">Country
                  <select
                    className="gf-input w-full mt-1"
                    value={genFilters.country}
                    onChange={(e) => setGenFilters({...genFilters, country: e.target.value})}
                    disabled={_invoiceCountriesFor(genFilters.network).length <= 1}
                    data-testid="gen-filter-country"
                    title={
                      genFilters.network === "pipeline"
                        ? "Pipeline is Canada-only."
                        : "Filter invoice cohort by country. Invoice cohorts are strictly single-country — All+All is refused by the backend."
                    }
                  >
                    {/* P4-N5B SLICE C4 R1 — Country options driven ENTIRELY
                        by the C1 capability matrix. A single-capability
                        network (Pipeline = CA) renders exactly one option
                        and disables the selector. Invoice-cohort
                        validity is enforced at submit time via
                        `isInvoiceCombinationValid`. */}
                    {_invoiceCountriesFor(genFilters.network).map((c) => (
                      <option key={c || "all"} value={c}>{COUNTRY_LABELS[c]}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs">Card
                  <input className="gf-input w-full mt-1" value={genFilters.card}
                    onChange={(e) => setGenFilters({...genFilters, card: e.target.value})}
                    data-testid="gen-filter-card" placeholder="card number" />
                </label>
                <label className="text-xs">Driver
                  <input className="gf-input w-full mt-1" value={genFilters.driver}
                    onChange={(e) => setGenFilters({...genFilters, driver: e.target.value})}
                    data-testid="gen-filter-driver" placeholder="driver name" />
                </label>
                <label className="text-xs">Unit
                  <input className="gf-input w-full mt-1" value={genFilters.unit}
                    onChange={(e) => setGenFilters({...genFilters, unit: e.target.value})}
                    data-testid="gen-filter-unit" placeholder="unit #" />
                </label>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button className="gf-btn gf-btn-primary gf-btn-sm" onClick={runPreview}
                  disabled={genLoading} data-testid="gen-run-preview">
                  {genLoading ? "Loading…" : "Preview transactions"}
                </button>
                <button className="gf-btn gf-btn-outline gf-btn-sm" onClick={selectAllEligible}
                  disabled={eligibleRows.length === 0} data-testid="gen-select-all-eligible">
                  Select all eligible ({eligibleRows.length})
                </button>
                <button className="gf-btn gf-btn-outline gf-btn-sm" onClick={clearSelection}
                  data-testid="gen-clear-selection">
                  Clear selection
                </button>
                <div className="ml-auto text-xs font-mono text-zinc-600 flex gap-3">
                  <span data-testid="gen-count-selected">Selected: {genSelected.size}</span>
                  <span data-testid="gen-count-eligible">Eligible: {eligibleRows.length}</span>
                  <span data-testid="gen-count-excluded">Excluded / Already Invoiced: {excludedCount}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="gf-table">
                  <thead>
                    <tr>
                      <th />
                      <th>DATE</th>
                      <th>NETWORK</th>
                      <th>CARD</th>
                      <th>DRIVER</th>
                      <th>UNIT</th>
                      <th>STATION</th>
                      <th>PRODUCT</th>
                      <th className="text-right">QTY</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {genRows.map((r) => {
                      const isEligible = eligible(r);
                      const rejectionReason = rejectedById.get(r.transaction_id);
                      const rowCls = rejectionReason
                        ? "bg-red-50"
                        : (!isEligible ? "opacity-60" : "");
                      return (
                        <tr key={r.transaction_id} data-testid={`gen-row-${r.transaction_id}`}
                          className={rowCls}>
                          <td>
                            <input type="checkbox" disabled={!isEligible || !!rejectionReason}
                              checked={genSelected.has(r.transaction_id)}
                              onChange={() => toggleRow(r.transaction_id)}
                              data-testid={`gen-select-${r.transaction_id}`} />
                          </td>
                          <td className="font-mono text-xs">{r.tran_date || r.transaction_date || "—"}</td>
                          <td className="font-mono text-xs uppercase">{r.network || "—"}</td>
                          <td className="font-mono text-xs">{r.card_number || "—"}</td>
                          <td className="text-xs">{r.driver_name || "—"}</td>
                          <td className="text-xs">{r.unit || "—"}</td>
                          <td className="text-xs">{r.station_name || r.station_id || "—"}</td>
                          <td className="text-xs">{r.product || r.product_canonical || "—"}</td>
                          <td className="gf-num text-xs">{r.quantity ?? r.qty ?? "—"}</td>
                          <td className="text-xs">
                            {rejectionReason ? (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold"
                                data-testid={`gen-row-rejected-${r.transaction_id}`}
                                title={`Server-rejected on last generate: ${rejectionReason}`}
                              >
                                Rejected — {friendlyReason(rejectionReason)}
                              </span>
                            ) : (
                              <span className={
                                !isEligible && r.invoice_id ? "text-zinc-500"
                                : isEligible ? "text-emerald-700 font-semibold" : "text-zinc-500"
                              }>
                                {r.invoice_id ? "Already Invoiced" : (isEligible ? "Eligible" : (r.status || "—"))}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {genRows.length === 0 && !genLoading && (
                      <tr><td colSpan={10} className="text-center text-zinc-400 py-8">
                        Click <b>Preview transactions</b> to load this customer's transactions in the selected range.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Step 2B-1 — rejected-id banner (all-or-nothing 409). */}
              {genRejected && (
                <div className="border border-red-200 bg-red-50 rounded p-3 text-xs" data-testid="gen-rejected-banner">
                  <div className="font-semibold text-red-800 mb-1">
                    Invoice not generated — {genRejected.rejected.length} selected transaction(s) are no longer eligible.
                  </div>
                  <div className="text-red-700 mb-2">{genRejected.message}</div>
                  <ul className="font-mono text-red-700 space-y-0.5 max-h-40 overflow-auto">
                    {genRejected.rejected.slice(0, 20).map((r) => (
                      <li key={r.transaction_id}>
                        <span className="mr-2">{r.transaction_id}</span>
                        <span className="text-red-500">→ {r.reason}</span>
                      </li>
                    ))}
                    {genRejected.rejected.length > 20 && (
                      <li className="text-red-500">…and {genRejected.rejected.length - 20} more</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Step 2B-1 — success card (visible after a manual invoice is generated). */}
              {genResult && (
                <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-sm flex flex-wrap items-center gap-3" data-testid="gen-success-card">
                  <div className="font-semibold text-emerald-800">
                    Invoice {genResult.invoice_number} generated for {genResult.customer_name}
                  </div>
                  <div className="font-mono text-xs text-emerald-700">
                    Lines: {genResult.lines} · Total: ${(genResult.total || 0).toFixed(2)}
                    {genResult.issue_date && ` · Issued ${genResult.issue_date}`}
                    {genResult.period_start && genResult.period_end && ` · Period ${genResult.period_start} → ${genResult.period_end}`}
                  </div>
                  <div className="ml-auto flex gap-2">
                    <Link to={`/invoices/${genResult.invoice_id}`} className="gf-btn gf-btn-outline gf-btn-sm" data-testid="gen-success-view-invoice">
                      View Invoice
                    </Link>
                    <button className="gf-btn gf-btn-outline gf-btn-sm"
                      onClick={() => { setLevel("months"); setGenResult(null); }}
                      data-testid="gen-success-back-to-invoices">
                      Back to Customer Invoices
                    </button>
                  </div>
                </div>
              )}

              <div className="border-t pt-3 flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs text-zinc-500 font-mono">
                  Step 2B-1 · manual invoice generation. Only the {genSelected.size} selected eligible transaction(s) will be invoiced. Existing invoice-calculation logic is used unchanged.
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* PHASE-4-P4-INVOICE-DATE-SELECTION-R1 (2026-02) —
                      operator-selectable Invoice Date / Due Date. Both
                      feed BOTH Preview PDF and Generate Invoice. If the
                      operator leaves them at their defaults, behaviour
                      is bit-identical to pre-R1 (today / today+14d). */}
                  <div className="flex items-center gap-1.5" data-testid="gen-invoice-dates-block">
                    <label className="text-xs text-zinc-600 font-mono" htmlFor="gen-invoice-date-input">
                      Invoice Date
                    </label>
                    <input
                      id="gen-invoice-date-input"
                      type="date"
                      value={genIssueDate}
                      onChange={(e) => setGenIssueDate(e.target.value)}
                      className="gf-input gf-input-sm font-mono"
                      style={{ width: "10.5rem" }}
                      required
                      data-testid="gen-invoice-date"
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-zinc-600 font-mono" htmlFor="gen-due-date-input">
                      Due Date
                    </label>
                    <input
                      id="gen-due-date-input"
                      type="date"
                      value={genDueDate}
                      onChange={(e) => setGenDueDate(e.target.value)}
                      min={genIssueDate || undefined}
                      className="gf-input gf-input-sm font-mono"
                      style={{ width: "10.5rem" }}
                      required
                      data-testid="gen-due-date"
                    />
                  </div>
                  {genDatesInvalid && (
                    <span
                      className="text-xs text-red-700 font-mono"
                      data-testid="gen-dates-error"
                    >
                      Due Date cannot be earlier than Invoice Date.
                    </span>
                  )}
                  {/* Step 2C-1 — Preview + Preflight CSV actions.
                      Both use exactly the currently selected transactions.
                      Neither triggers invoice generation or mutates data. */}
                  <button
                    className="gf-btn gf-btn-outline gf-btn-sm"
                    onClick={openInvoicePreview}
                    disabled={
                      genSelected.size === 0
                      || previewLoading
                      || !previewTotals
                      || previewInvalid
                      || previewedIdsKey !== selectionKey
                      || genDatesInvalid
                    }
                    data-testid="gen-preview-invoice-btn"
                    title={genSelected.size === 0
                      ? "Select at least one eligible transaction"
                      : genDatesInvalid
                        ? "Fix the invoice/due date pair before previewing"
                        : previewLoading || !previewTotals || previewedIdsKey !== selectionKey
                          ? "Waiting for preview totals to reflect the current selection"
                          : "Download the draft-invoice PDF directly. Non-persistent — no invoice is created."}
                  >
                    Preview PDF
                  </button>
                  <button
                    className="gf-btn gf-btn-outline gf-btn-sm"
                    onClick={downloadPreflightCsv}
                    disabled={genSelected.size === 0}
                    data-testid="gen-preflight-csv-btn"
                    title={genSelected.size === 0
                      ? "Select at least one eligible transaction"
                      : "Download the selected transactions as a CSV preview. No invoice is created."}
                  >
                    Download Preflight CSV
                  </button>
                  <button
                    className="gf-btn gf-btn-primary"
                    onClick={() => setGenConfirmOpen(true)}
                    disabled={
                      genSelected.size === 0
                      || genSubmitting
                      || previewLoading
                      || !previewTotals
                      || previewedIdsKey !== selectionKey
                      || genDatesInvalid
                    }
                    data-testid="gen-generate-invoice-btn"
                    title={
                      genSelected.size === 0
                        ? "Select at least one eligible transaction"
                        : genDatesInvalid
                          ? "Fix the invoice/due date pair before generating"
                          : previewLoading || !previewTotals || previewedIdsKey !== selectionKey
                            ? "Waiting for preview totals to reflect the current selection"
                            : "Generate an invoice from the selected transactions"
                    }
                  >
                    {genSubmitting ? "Generating…" : `Generate Invoice (${genSelected.size})`}
                  </button>
                </div>
              </div>

              {/* PHASE-4-P4-INVOICE-V2-SERVICE-CHARGES-DISCOUNT-AND-LABELS R1
                  (Feb 2026) — additive Invoice Adjustments controls.
                  Optional; blank/zero = no financial effect. Server
                  computes Service Charge tax authoritatively from
                  `customer.billing_province` (Option A). USA invoices
                  force service-charge tax = 0. */}
              <div
                className="border rounded p-3 bg-white space-y-2"
                data-testid="gen-adjustments-block"
              >
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  Invoice Adjustments (optional)
                </div>
                {/* Service Charges row */}
                <div className="grid grid-cols-12 gap-2 items-center" data-testid="gen-service-charge-row">
                  <label className="col-span-2 text-xs text-zinc-600 font-mono">
                    Service Charges
                  </label>
                  <input
                    type="text"
                    placeholder="Description (e.g. Fuel Management Fee)"
                    className="gf-input gf-input-sm col-span-5"
                    value={genServiceChargeDesc}
                    onChange={(e) => setGenServiceChargeDesc(e.target.value)}
                    data-testid="gen-service-charge-description"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Amount"
                    className="gf-input gf-input-sm col-span-2 text-right font-mono"
                    value={genServiceChargeAmount}
                    onChange={(e) => setGenServiceChargeAmount(e.target.value)}
                    data-testid="gen-service-charge-amount"
                  />
                  <select
                    className="gf-input gf-input-sm col-span-3 font-mono text-xs"
                    value={genServiceChargeApplyTax ? "apply" : "none"}
                    onChange={(e) => setGenServiceChargeApplyTax(e.target.value === "apply")}
                    disabled={(previewTotals?.currency || "CAD").toUpperCase() === "USD"}
                    data-testid="gen-service-charge-tax-select"
                    title={
                      (previewTotals?.currency || "CAD").toUpperCase() === "USD"
                        ? "USA invoice — no sales tax on service charges"
                        : "Applies the customer's billing-province sales tax"
                    }
                  >
                    {(previewTotals?.currency || "CAD").toUpperCase() === "USD" ? (
                      <option value="none" data-testid="gen-sc-tax-opt-none-usa">
                        N/A (USA — no sales tax)
                      </option>
                    ) : (
                      <>
                        <option value="apply" data-testid="gen-sc-tax-opt-apply">
                          {`Apply ${CA_TAX_DISPLAY[(previewTotals?.billing_province || "").toUpperCase()] || "GST/HST (billing province)"}`}
                        </option>
                        <option value="none" data-testid="gen-sc-tax-opt-none">
                          No tax
                        </option>
                      </>
                    )}
                  </select>
                </div>
                {/* Discount row */}
                <div className="grid grid-cols-12 gap-2 items-center" data-testid="gen-discount-row">
                  <label className="col-span-2 text-xs text-zinc-600 font-mono">
                    Discount
                  </label>
                  <input
                    type="text"
                    placeholder="Description (e.g. Monthly Volume Discount)"
                    className="gf-input gf-input-sm col-span-7"
                    value={genDiscountDesc}
                    onChange={(e) => setGenDiscountDesc(e.target.value)}
                    data-testid="gen-discount-description"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Amount"
                    className="gf-input gf-input-sm col-span-3 text-right font-mono"
                    value={genDiscountAmount}
                    onChange={(e) => setGenDiscountAmount(e.target.value)}
                    data-testid="gen-discount-amount"
                  />
                </div>
                <div className="text-xs text-zinc-500 font-mono">
                  Service Charges add to the total; Discount subtracts. Blank or 0 leaves the invoice unchanged.
                </div>
              </div>

              {/* Step 2C-2A — Preview Totals block. Read-only. Reuses the
                  same authoritative `_price_selected_ids` helper the
                  Generate endpoint uses (see backend/routes/invoices.py).
                  Renders ONLY when a non-empty selection exists so the
                  block never shouts $0.00 at an idle admin.
                  ALL-OR-NOTHING: if the server rejects any id, totals
                  stay hidden and a warning tells the admin to re-preview. */}
              {genSelected.size > 0 && (
                <div
                  className="border rounded p-3 bg-zinc-50"
                  data-testid="gen-preview-totals-block"
                >
                  <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
                    Preview Totals
                  </div>
                  {previewLoading && (
                    <div
                      className="text-sm text-zinc-500 italic"
                      data-testid="gen-preview-totals-loading"
                    >
                      Calculating…
                    </div>
                  )}
                  {!previewLoading && previewInvalid && (
                    <div
                      className="text-sm text-red-700"
                      data-testid="gen-preview-totals-invalid"
                    >
                      One or more selected transactions are no longer
                      eligible. Refresh the preview or un-tick the
                      rejected rows before generating.
                    </div>
                  )}
                  {!previewLoading && !previewInvalid && previewTotals && (
                    <div
                      className="grid grid-cols-3 gap-x-6 gap-y-1 text-sm font-mono max-w-md"
                      data-testid="gen-preview-totals-values"
                    >
                      <div className="col-span-2 text-zinc-600">Subtotal</div>
                      <div
                        className="text-right"
                        data-testid="gen-preview-totals-subtotal"
                      >
                        ${(previewTotals.subtotal || 0).toFixed(2)}
                      </div>
                      {/* P4-INVOICE-V2-SERVICE-CHARGES-DISCOUNT-AND-LABELS R1 —
                          Optional Service Charges + Discount rows echoed by
                          the server-side _compute_adjustments helper. Shown
                          only when the amount > 0. */}
                      {previewTotals.service_charge && (previewTotals.service_charge.amount || 0) > 0 && (
                        <React.Fragment>
                          <div className="col-span-2 text-zinc-600" data-testid="gen-preview-totals-service-charge-label">
                            Service Charges
                            {previewTotals.service_charge.description
                              ? ` — ${previewTotals.service_charge.description}`
                              : ""}
                          </div>
                          <div className="text-right" data-testid="gen-preview-totals-service-charge-amount">
                            ${(previewTotals.service_charge.amount || 0).toFixed(2)}
                          </div>
                          {(previewTotals.service_charge.tax_amount || 0) > 0 && (
                            <React.Fragment>
                              <div className="col-span-2 text-zinc-500 text-xs pl-2" data-testid="gen-preview-totals-service-charge-tax-label">
                                {`${previewTotals.service_charge.tax_type || "Tax"} ${((previewTotals.service_charge.tax_rate || 0) * 100).toFixed(2).replace(/\.?0+$/, "")}% (service)`}
                              </div>
                              <div className="text-right text-zinc-500 text-xs" data-testid="gen-preview-totals-service-charge-tax-amount">
                                ${(previewTotals.service_charge.tax_amount || 0).toFixed(2)}
                              </div>
                            </React.Fragment>
                          )}
                        </React.Fragment>
                      )}
                      {previewTotals.discount && (previewTotals.discount.amount || 0) > 0 && (
                        <React.Fragment>
                          <div className="col-span-2 text-zinc-600" data-testid="gen-preview-totals-discount-label">
                            Discount
                            {previewTotals.discount.description
                              ? ` — ${previewTotals.discount.description}`
                              : ""}
                          </div>
                          <div className="text-right text-red-700" data-testid="gen-preview-totals-discount-amount">
                            -${(previewTotals.discount.amount || 0).toFixed(2)}
                          </div>
                        </React.Fragment>
                      )}
                      {Array.isArray(previewTotals.tax_breakdown) &&
                      previewTotals.tax_breakdown.length > 0 ? (
                        <>
                          {previewTotals.tax_breakdown.map((br, idx) => {
                            const province = String(br.province || "").toUpperCase();
                            const type = String(br.type || "").toUpperCase();
                            const rateLabel = Number(
                              (Number(br.rate) * 100).toFixed(2)
                            ).toString();
                            const rowKey = `${province}-${type}-${rateLabel}-${idx}`;
                            const testIdSuffix = `${province}-${type}-${rateLabel}`.toLowerCase();
                            return (
                              <React.Fragment key={rowKey}>
                                <div
                                  className="col-span-2 text-zinc-600"
                                  data-testid={`gen-preview-totals-breakdown-row-${testIdSuffix}`}
                                >
                                  {province} {type} {rateLabel}%
                                </div>
                                <div
                                  className="text-right"
                                  data-testid={`gen-preview-totals-breakdown-amount-${testIdSuffix}`}
                                >
                                  ${(Number(br.amount) || 0).toFixed(2)}
                                </div>
                              </React.Fragment>
                            );
                          })}
                          <div
                            className="col-span-2 text-zinc-600"
                            data-testid="gen-preview-totals-total-tax-label"
                          >
                            Total Tax
                          </div>
                          <div
                            className="text-right"
                            data-testid="gen-preview-totals-tax"
                          >
                            ${(previewTotals.display_total_tax != null
                                ? previewTotals.display_total_tax
                                : (previewTotals.tax_amount || 0)).toFixed(2)}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="col-span-2 text-zinc-600">
                            Tax {previewTotals.tax_rate != null
                              ? `(${(previewTotals.tax_rate * 100).toFixed(2)}%)`
                              : ""}
                          </div>
                          <div
                            className="text-right"
                            data-testid="gen-preview-totals-tax"
                          >
                            ${(previewTotals.display_total_tax != null
                                ? previewTotals.display_total_tax
                                : (previewTotals.tax_amount || 0)).toFixed(2)}
                          </div>
                        </>
                      )}
                      <div className="col-span-2 font-semibold text-zinc-900 border-t pt-1">
                        Total
                      </div>
                      <div
                        className="text-right font-semibold border-t pt-1"
                        data-testid="gen-preview-totals-total"
                      >
                        ${(previewTotals.final_total != null
                            ? previewTotals.final_total
                            : (previewTotals.total || 0)).toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2B-1 — confirmation modal (no financial totals). */}
              {genConfirmOpen && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                  data-testid="gen-confirm-modal"
                  onClick={(e) => { if (e.target === e.currentTarget && !genSubmitting) setGenConfirmOpen(false); }}
                >
                  <div className="gf-card max-w-md w-full mx-4">
                    <div className="gf-card-body space-y-3">
                      <div className="font-semibold text-lg">Generate invoice for {selectedCustomerName}?</div>
                      <div className="text-sm">
                        Selected transactions: <b data-testid="gen-confirm-count">{genSelected.size}</b>
                      </div>
                      <div className="text-xs text-zinc-500">
                        This will create an invoice using the system's existing invoice calculation rules.
                        If any selected transaction is no longer eligible, no invoice will be created and no
                        transactions will be changed.
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          className="gf-btn gf-btn-outline"
                          onClick={() => setGenConfirmOpen(false)}
                          disabled={genSubmitting}
                          data-testid="gen-confirm-cancel"
                        >
                          Cancel
                        </button>
                        <button
                          className="gf-btn gf-btn-primary"
                          onClick={submitGenerate}
                          disabled={genSubmitting}
                          data-testid="gen-confirm-submit"
                        >
                          {genSubmitting ? "Generating…" : "Generate Invoice"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {level === "customers" && (
        <div className="gf-card" data-testid="invoices-level-customers">
          <div className="gf-card-body">
            <div className="flex items-center gap-4 mb-3">
              <input
                type="text"
                placeholder="Search customer by name…"
                className="gf-input w-full"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                data-testid="invoices-customer-search"
              />
              <div className="shrink-0">
                <ActiveOnlySwitch checked={invActiveOnly} onChange={setInvActiveOnly} testId="invoices-active-only-toggle" />
              </div>
              <span className="overline shrink-0" data-testid="invoices-customer-count">{filteredCustomers.length} CUSTOMERS</span>
            </div>
            <div className="overflow-x-auto">
              <table className="gf-table">
                <thead>
                  <tr>
                    <th>CUSTOMER</th>
                    <th>BILLING DAYS (WEEKLY)</th>
                    <th className="text-right"># INVOICES</th>
                    <th>LATEST INVOICE DATE</th>
                    <th>LATEST INVOICE #</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((c) => (
                    <tr key={c.customer_id} data-testid={`invoices-customer-row-${c.customer_id}`}>
                      <td className="text-sm font-semibold">{c.customer_name || "—"}</td>
                      <td className="font-mono text-xs" data-testid={`invoices-billing-days-${c.customer_id}`}>{formatBillingDays(c.billing_days)}</td>
                      <td className="gf-num">{c.invoice_count}</td>
                      <td className="font-mono text-xs">{c.latest_issue_date || "—"}</td>
                      <td className="font-mono text-xs">{c.latest_invoice_number || "—"}</td>
                      <td className="text-right">
                        <button
                          className="gf-btn gf-btn-outline gf-btn-sm"
                          onClick={() => { setSelectedCustomerId(c.customer_id); setLevel("months"); }}
                          data-testid={`invoices-view-customer-${c.customer_id}`}
                        >
                          View invoices
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-zinc-400 py-8">
                      {customerSearch ? "No customers match your search." : "No invoices in this view."}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {level === "months" && (
        <div className="gf-card" data-testid="invoices-level-months">
          <div className="gf-card-body">
            <div className="overflow-x-auto">
              <table className="gf-table">
                <thead>
                  <tr>
                    <th>MONTH</th>
                    <th className="text-right"># INVOICES</th>
                    <th>LATEST INVOICE DATE</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {monthGroups.map((m) => (
                    <tr key={m.month_key} data-testid={`invoices-month-row-${m.month_key}`}>
                      <td className="font-semibold">{monthLabel(m.month_key)}</td>
                      <td className="gf-num">{m.invoice_count}</td>
                      <td className="font-mono text-xs">{m.latest_issue_date || "—"}</td>
                      <td className="text-right">
                        <button
                          className="gf-btn gf-btn-outline gf-btn-sm"
                          onClick={() => { setSelectedMonth(m.month_key); setLevel("invoices"); }}
                          data-testid={`invoices-view-month-${m.month_key}`}
                        >
                          View invoices
                        </button>
                      </td>
                    </tr>
                  ))}
                  {monthGroups.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-zinc-400 py-8">
                      No invoices for this customer in the current view.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {level === "invoices" && (
      <div className="gf-card" data-testid="invoices-level-leaf">
        <div className="overflow-x-auto">
          <table className="gf-table">
            <thead>
              <tr>
                <th>#</th><th>CUSTOMER</th><th>ISSUED</th><th>DUE</th><th>PERIOD</th>
                <th className="text-right">SUBTOTAL</th><th className="text-right">TAX</th><th className="text-right">TOTAL</th><th className="text-right">PAID</th>
                <th className="text-right">GP</th><th className="text-right">MARGIN%</th>
                <th>STATUS</th><th></th>
              </tr>
            </thead>
            <tbody>
              {leafRows.map((i) => {
                const low = isLowMargin(i);
                return (
                  <tr key={i.invoice_id} style={low ? { background: "#FEE2E2" } : {}}>
                    <td><Link to={`/invoices/${i.invoice_id}`} className="font-mono text-xs font-semibold hover:underline" data-testid={`invoice-link-${i.invoice_id}`}>{i.invoice_number}</Link></td>
                    <td className="text-sm">{i.customer_name}</td>
                    <td className="font-mono text-xs">{i.issue_date}</td>
                    <td className="font-mono text-xs">{i.due_date}</td>
                    <td className="font-mono text-xs">{i.period_start} → {i.period_end}</td>
                    <td className="gf-num">${i.subtotal?.toFixed(2)}</td>
                    <td className="gf-num">${i.tax_amount?.toFixed(2)}</td>
                    <td className="gf-num font-semibold">${i.total?.toFixed(2)}</td>
                    <td className="gf-num">${(i.paid_amount || 0).toFixed(2)}</td>
                    <td className="gf-num" data-testid={`invoice-gp-${i.invoice_id}`}>{typeof i.gross_profit === "number" ? `$${i.gross_profit.toFixed(2)}` : "—"}</td>
                    <td className="gf-num" style={low ? { color: "#dc2626", fontWeight: 600 } : {}} data-testid={`invoice-margin-${i.invoice_id}`}>
                      {typeof i.margin_pct === "number" ? `${i.margin_pct.toFixed(2)}%` : "—"}
                    </td>
                    <td><Badge variant={i.status === "paid" ? "success" : i.status === "overdue" ? "error" : "info"}>{i.status.replace("_", " ")}</Badge></td>
                    <td className="text-right whitespace-nowrap">
                      <a className="gf-btn gf-btn-ghost" href="#"
                         onClick={(e) => { e.preventDefault(); downloadInvoicePdf(i.invoice_id); }}
                         data-testid={`invoice-pdf-${i.invoice_id}`}><DownloadSimple size={14}/></a>
                      <button
                        className="gf-btn gf-btn-ghost"
                        style={{ color: "#dc2626" }}
                        onClick={() => setDeleteTarget(i)}
                        data-testid={`invoice-delete-${i.invoice_id}`}
                        title="Permanently delete this invoice (irreversible)"
                      >
                        <Trash size={14}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {leafRows.length === 0 && (
                <tr><td colSpan={13} className="text-center text-zinc-400 py-8">
                  {lowOnly ? `No invoices below ${thresholdPct}% margin.` : "No invoices in this view."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Per-invoice irreversible delete confirmation. Same UX as the
          Transactions page (Upload History tile) — typed phrase + impact
          summary. Calls the backend's hard-delete path which: (a) detaches
          every transaction line back to `pending` (so they can be invoiced
          again), and (b) removes the invoice document permanently. */}
      <DestructiveConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={`Permanently delete invoice ${deleteTarget?.invoice_number || ""}`}
        impactSummary={
          deleteTarget
            ? `This will permanently remove invoice ${deleteTarget.invoice_number} (total $${(deleteTarget.total || 0).toFixed(2)}, customer ${deleteTarget.customer_name || "—"}). Its ${deleteTarget.line_count ?? "associated"} transaction line(s) will be detached and returned to "pending" so you can re-invoice them.`
            : ""
        }
        requiredText="I UNDERSTAND THIS IS IRREVERSIBLE"
        confirmButtonLabel="Permanently delete invoice"
        onConfirm={async ({ confirmText }) => {
          await api.delete(`/invoices/${deleteTarget.invoice_id}`, {
            data: {
              hard_delete: true,
              confirm: confirmText,
              reason: "operator_delete_from_invoices_page",
            },
          });
          toast.success(`Invoice ${deleteTarget.invoice_number} permanently deleted`);
          load();
        }}
      />
    </SectionPad>
  );
}

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
function formatBillingDays(days) {
  const set = new Set((days || []).map((d) => String(d).toLowerCase().slice(0, 3)));
  const out = WEEKDAYS.filter((d) => set.has(d)).map((d) => d.toUpperCase());
  return out.length ? out.join(", ") : "—";
}
